import React, { useState, useEffect, useCallback } from 'react';
import { projectsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { AppDrawer } from '../ui/AppDrawer';
import { Plus, Loader2, Calendar, Cloud, Sun, CloudRain, Snowflake, Trash2, Edit2, Users, Clock } from 'lucide-react';
import { toast } from 'sonner';

const WEATHER_OPTIONS = ['Clear', 'Partly Cloudy', 'Overcast', 'Rain', 'Snow', 'Windy', 'Hot', 'Cold'];

export function ProjectDailyLogs({ projectId, canEdit, teamUsers }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    log_date: new Date().toISOString().split('T')[0],
    crew_on_site: [], arrival_time: '', departure_time: '',
    weather: '', notes: '', blockers: '', tomorrow_plan: '',
  });

  const fetchLogs = useCallback(async () => {
    try {
      const res = await projectsAPI.listDailyLogs(projectId);
      setLogs(res.data.logs || []);
    } catch {
      toast.error('Failed to load daily logs');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const openCreate = () => {
    setEditingLog(null);
    setForm({
      log_date: new Date().toISOString().split('T')[0],
      crew_on_site: [], arrival_time: '07:00', departure_time: '16:00',
      weather: '', notes: '', blockers: '', tomorrow_plan: '',
    });
    setDrawerOpen(true);
  };

  const openEdit = (log) => {
    setEditingLog(log);
    setForm({
      log_date: log.log_date || '', crew_on_site: log.crew_on_site || [],
      arrival_time: log.arrival_time || '', departure_time: log.departure_time || '',
      weather: log.weather || '', notes: log.notes || '',
      blockers: log.blockers || '', tomorrow_plan: log.tomorrow_plan || '',
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!form.log_date) { toast.error('Date is required'); return; }
    setSaving(true);
    try {
      const payload = {
        log_date: form.log_date,
        crew_on_site: form.crew_on_site?.length > 0 ? form.crew_on_site : undefined,
        arrival_time: form.arrival_time || undefined,
        departure_time: form.departure_time || undefined,
        weather: form.weather || undefined,
        notes: form.notes || undefined,
        blockers: form.blockers || undefined,
        tomorrow_plan: form.tomorrow_plan || undefined,
      };
      if (editingLog) {
        await projectsAPI.updateDailyLog(projectId, editingLog.id, payload);
        toast.success('Log updated');
      } else {
        await projectsAPI.createDailyLog(projectId, payload);
        toast.success('Log added');
      }
      setDrawerOpen(false);
      fetchLogs();
    } catch { toast.error('Failed to save log'); } finally { setSaving(false); }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm('Delete this daily log?')) return;
    try {
      await projectsAPI.deleteDailyLog(projectId, logId);
      fetchLogs();
      toast.success('Log deleted');
    } catch { toast.error('Failed to delete log'); }
  };

  const toggleCrewMember = (user) => {
    setForm(f => {
      const existing = f.crew_on_site.find(c => c.user_id === user.id);
      if (existing) return { ...f, crew_on_site: f.crew_on_site.filter(c => c.user_id !== user.id) };
      return { ...f, crew_on_site: [...f.crew_on_site, { user_id: user.id, name: user.name }] };
    });
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4" data-testid="project-daily-logs">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">{logs.length} log{logs.length !== 1 ? 's' : ''}</h3>
        {canEdit && (
          <Button size="sm" onClick={openCreate} data-testid="new-daily-log-btn">
            <Plus className="w-4 h-4 mr-1" /> New Log
          </Button>
        )}
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="daily-logs-empty">
          <Calendar className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-lg font-medium">No daily logs yet</p>
          <p className="text-sm mt-1">Add daily logs to track progress on this project.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map(log => (
            <div key={log.id} className="border rounded-lg p-4" data-testid={`daily-log-${log.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{log.log_date}</span>
                    {log.weather && <span className="text-xs text-muted-foreground bg-muted rounded px-2 py-0.5">{log.weather}</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    {log.author_name && <span>By {log.author_name}</span>}
                    {log.arrival_time && log.departure_time && (
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{log.arrival_time} - {log.departure_time}</span>
                    )}
                    {log.crew_on_site?.length > 0 && (
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{log.crew_on_site.length} on site</span>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(log)} className="text-muted-foreground hover:text-foreground" data-testid={`edit-log-${log.id}`}><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(log.id)} className="text-muted-foreground hover:text-red-500" data-testid={`delete-log-${log.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>

              {log.crew_on_site?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {log.crew_on_site.map((c, i) => <span key={i} className="text-xs bg-muted rounded px-2 py-0.5">{c.name}</span>)}
                </div>
              )}

              {log.notes && <div className="text-sm mb-2"><p className="whitespace-pre-wrap">{log.notes}</p></div>}
              {log.blockers && (
                <div className="text-sm mb-2 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded p-2">
                  <span className="font-medium text-red-700 dark:text-red-400 text-xs uppercase">Blockers: </span>
                  <span className="text-red-700 dark:text-red-300">{log.blockers}</span>
                </div>
              )}
              {log.tomorrow_plan && (
                <div className="text-sm bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded p-2">
                  <span className="font-medium text-blue-700 dark:text-blue-400 text-xs uppercase">Tomorrow: </span>
                  <span className="text-blue-700 dark:text-blue-300">{log.tomorrow_plan}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Drawer */}
      <AppDrawer open={drawerOpen} onOpenChange={setDrawerOpen} title={editingLog ? 'Edit Daily Log' : 'New Daily Log'} description={form.log_date}>
        <div className="space-y-4 p-1">
          <div>
            <Label>Date *</Label>
            <Input type="date" value={form.log_date} onChange={e => setForm(f => ({...f, log_date: e.target.value}))} disabled={!!editingLog} data-testid="log-date-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Arrival</Label><Input type="time" value={form.arrival_time} onChange={e => setForm(f => ({...f, arrival_time: e.target.value}))} /></div>
            <div><Label>Departure</Label><Input type="time" value={form.departure_time} onChange={e => setForm(f => ({...f, departure_time: e.target.value}))} /></div>
          </div>
          <div>
            <Label>Weather</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {WEATHER_OPTIONS.map(w => (
                <button key={w} onClick={() => setForm(f => ({...f, weather: f.weather === w ? '' : w}))} className={`px-2.5 py-1 rounded text-xs border transition-colors ${form.weather === w ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}>
                  {w}
                </button>
              ))}
            </div>
          </div>
          {teamUsers.length > 0 && (
            <div>
              <Label>Crew on Site</Label>
              <div className="flex flex-wrap gap-1 mt-1 max-h-[120px] overflow-y-auto">
                {teamUsers.map(u => {
                  const selected = form.crew_on_site.some(c => c.user_id === u.id);
                  return (
                    <button key={u.id} onClick={() => toggleCrewMember(u)} className={`px-2.5 py-1 rounded text-xs border transition-colors ${selected ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}>
                      {u.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div><Label>Notes / Work Performed</Label><Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={4} placeholder="What was accomplished today..." data-testid="log-notes-input" /></div>
          <div><Label>Blockers / Issues</Label><Textarea value={form.blockers} onChange={e => setForm(f => ({...f, blockers: e.target.value}))} rows={2} placeholder="Any blockers or issues..." /></div>
          <div><Label>Tomorrow's Plan</Label><Textarea value={form.tomorrow_plan} onChange={e => setForm(f => ({...f, tomorrow_plan: e.target.value}))} rows={2} placeholder="What's planned for tomorrow..." /></div>
          <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-daily-log-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {editingLog ? 'Update Log' : 'Add Log'}
          </Button>
        </div>
      </AppDrawer>
    </div>
  );
}
